/**
 * LostLockModal — surfaces when the user's edit lock was lost. Three causes,
 * with tailored copy for each:
 *   1. The user opened the same event in another of their own tabs (auto-takeover).
 *   2. Another user explicitly clicked "Take over editing".
 *   3. The lock TTL expired silently while the user was idle past 10 min.
 *
 * In all cases the form is stale relative to whatever the new editor sees, so
 * the user MUST reload — but we offer copy-to-clipboard for unsaved typing first.
 */

import React, { useState } from 'react';
import { buttonClass } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { TextArea } from './ui/Field';
import { IconWarning } from './ui/icons';
import { LockHolder } from '../utils/useEditLock';
import useUserStore from '../stores/useUserStore';

interface Props {
  holder: LockHolder | null;
  /** JSON string of the dirty form fields, supplied by the parent form. */
  dirtySnapshot: string | null;
  onAcknowledge: () => void;
}

const LostLockModal: React.FC<Props> = ({ holder, dirtySnapshot, onAcknowledge }) => {
  const [copied, setCopied] = useState(false);
  const currentUserId = useUserStore((s) => s.user.id);
  const newEditorName = holder?.user_name;
  // String() both sides — user IDs may be UUID strings or numeric depending on schema.
  const heldByCurrentUser = !!(holder?.user_id && String(holder.user_id) === String(currentUserId));

  const copyToClipboard = async () => {
    if (!dirtySnapshot) return;
    try {
      await navigator.clipboard.writeText(dirtySnapshot);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — text is selectable below as a fallback
    }
  };

  const heading = heldByCurrentUser ? 'You opened this in another tab' : 'Your edit session ended';

  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      hideClose
      alert
      size="xl"
      title={
        <span className="flex items-center gap-3 text-amber-200">
          <IconWarning className="h-6 w-6 shrink-0 text-amber-400" />
          {heading}
        </span>
      }
      footer={
        <button type="button" onClick={onAcknowledge} className={buttonClass('filled', 'md')}>
          Reload page
        </button>
      }
    >
      <div className="space-y-3">
        <p className="m-0 text-sm text-on-surface">
          {heldByCurrentUser
            ? <>You opened this event in another browser tab, so editing on this tab has ended. Switch to the other tab to keep editing, or copy any unsaved changes here first, then reload.</>
            : newEditorName
              ? <><strong className="text-amber-200">{newEditorName}</strong> is now editing this event. Your edit session here has ended.</>
              : <>Someone else is now editing this event. Your edit session here has ended.</>
          }
        </p>
        <p className="m-0 text-sm text-on-surface">
          To prevent overwriting {heldByCurrentUser ? 'changes from your other tab' : 'their changes'}, your unsaved edits cannot be saved. The page will reload to show the current version. Copy anything you need first.
        </p>

        {dirtySnapshot && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Your unsaved changes</label>
              <button
                type="button"
                onClick={copyToClipboard}
                className={buttonClass('quiet', 'sm')}
              >
                {copied ? 'Copied!' : 'Copy to clipboard'}
              </button>
            </div>
            <TextArea
              readOnly
              value={dirtySnapshot}
              className="h-48 font-mono"
              onClick={(e) => (e.currentTarget as HTMLTextAreaElement).select()}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default LostLockModal;
